<template>
  <div class="leave-calendar">
    <div class="container">
      <div class="calendar-grid">
        <!-- Calendar Section -->
        <div class="calendar-section">
          <div class="card">
            <!-- Card Header -->
            <div class="card-header">
              <h2 class="card-title">Leave/Holiday Calendar</h2>
              <div class="month-navigation">
                <button @click="navigateMonth('prev')" class="nav-button">
                  <svg
                    class="icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <div class="flex items-center gap-2">
                  <select
                    v-model="selectedMonth"
                    @change="updateMonthYear"
                    class="month-select"
                  >
                    <option
                      v-for="(month, index) in monthNames"
                      :key="index"
                      :value="index"
                    >
                      {{ month }}
                    </option>
                  </select>
                  <select
                    v-model="selectedYear"
                    @change="updateMonthYear"
                    class="year-select"
                  >
                    <option
                      v-for="year in yearOptions"
                      :key="year"
                      :value="year"
                    >
                      {{ year }}
                    </option>
                  </select>
                </div>
                <button @click="navigateMonth('next')" class="nav-button">
                  <svg
                    class="icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content">
              <!-- Day headers -->
              <div class="day-headers">
                <div v-for="day in dayNames" :key="day" class="day-header">
                  {{ day }}
                </div>
              </div>

              <!-- Calendar grid -->
              <div class="calendar-days">
                <div
                  v-for="(dayInfo, index) in calendarDays"
                  :key="index"
                  class="day-cell"
                >
                  <button
                    @click="handleDateClick(dayInfo)"
                    :class="getDayClasses(dayInfo)"
                    class="day-button"
                  >
                    {{ dayInfo.day }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Details Section -->
        <div class="details-section">
          <!-- Selected Date Header -->
          <div class="card">
            <div class="card-content">
              <div class="selected-date-header">
                <svg
                  class="calendar-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h2 class="selected-date-title">{{ formattedSelectedDate }}</h2>
              </div>

              <div
                v-if="selectedHoliday"
                :class="
                  selectedHoliday.status === 'WEEKLY_OFF'
                    ? 'weeklyoff-banner'
                    : 'holiday-banner'
                "
              >
                <div
                  :class="
                    selectedHoliday.status === 'WEEKLY_OFF'
                      ? 'weeklyoff-indicator'
                      : 'holiday-indicator'
                  "
                ></div>
                <span
                  :class="
                    selectedHoliday.status === 'WEEKLY_OFF'
                      ? 'weeklyoff-text'
                      : 'holiday-text'
                  "
                >
                  {{ selectedHoliday.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- People on Leave -->
          <div v-if="selectedLeaveEntries.length > 0" class="card">
            <div class="card-header">
              <h3 class="section-title">
                <svg
                  class="users-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
                People on Leave
              </h3>
            </div>

            <div class="card-content">
              <div class="leave-entries">
                <div
                  v-for="entry in selectedLeaveEntries"
                  :key="entry.id"
                  class="leave-entry"
                >
                  <div class="avatar">
                    <span class="avatar-text">{{
                      getInitials(entry.name)
                    }}</span>
                  </div>
                  <div class="entry-details">
                    <div class="entry-header">
                      <span class="employee-name">{{ entry.name }}</span>
                      <span
                        :class="getBadgeClasses(entry.type) + ' leave-badge leave-red-text'"
                        style="color: #dc2626; background: #fef2f2; border: 1px solid #dc2626;"
                      >
                        Leave
                      </span>
                      <div class="duration-info">
                        <svg
                          class="clock-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span class="duration-text">{{ entry.duration }}</span>
                      </div>
                    </div>
                    <div v-if="entry.date && entry.toDate" class="leave-dates" style="margin-top: 2px; color: #6b7280; font-size: 13px;">
                      <span><b>From:</b> {{ formatDateTime(entry.date) }}</span>
                      <span style="margin-left: 10px;"><b>To:</b> {{ formatDateTime(entry.toDate) }}</span>
                    </div>
                    <p v-if="entry.reason" class="entry-reason">
                      <b>Reason:</b> {{ entry.reason }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Activity State -->
          <div
            v-if="selectedLeaveEntries.length === 0 && !selectedHoliday"
            class="card"
          >
            <div class="card-content no-activity">
              <div class="no-activity-icon">
                <svg
                  class="empty-calendar-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <p class="no-activity-text">
                No leave requests or holidays on this date
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";

export default {
  name: "LeaveCalendar",
  data() {
    return {
      currentDate: moment(),
      selectedDate: moment(),
      selectedMonth: moment().month(),
      selectedYear: moment().year(),
      yearOptions: this.generateYears(),
      monthNames: moment.months(),
      dayNames: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      // holidays: [
      //   {
      //     name: "Independence Day",
      //     date: moment("2025-08-15"),
      //     type: "public",
      //   },
      //   { name: "Christmas Day", date: moment("2025-12-25"), type: "public" },
      //   { name: "New Year", date: moment("2025-01-01"), type: "public" },
      // ],
      // leaveEntries: [
      //   {
      //     id: "1",
      //     name: "John Doe",
      //     type: "Sick Leave",
      //     duration: "Full Day",
      //     date: moment("2025-07-11"),
      //   },
      //   {
      //     id: "2",
      //     name: "Priya S",
      //     type: "Casual Leave",
      //     duration: "Half Day",
      //     reason: "Doctor's appointment",
      //     date: moment("2025-07-11"),
      //   },
      //   {
      //     id: "3",
      //     name: "Mike Johnson",
      //     type: "Annual Leave",
      //     duration: "Full Day",
      //     date: moment("2025-07-15"),
      //   },
      // ],
    };
  },
  computed: {
    ...mapState({
      calendarHolidays: (state) => state.holidayManagement.calendarHolidays,
    }),
    holidays() {
      // Unique holiday dates (status: HOLIDAY)
      const all = [];
      this.calendarHolidays &&
        this.calendarHolidays.forEach((user) => {
          user.unavailabilities.forEach((u) => {
            if (u.status === "HOLIDAY") {
              all.push({
                date: moment(u.date),
                name: u.description || "Holiday",
                type: "public",
                status: "HOLIDAY",
              });
            }
          });
        });
      // Remove duplicates by date
      const unique = [];
      const seen = new Set();
      all.forEach((h) => {
        const key = h.date.format("YYYY-MM-DD");
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(h);
        }
      });
      return unique;
    },
    weeklyOffs() {
      // Unique weekly off dates (status: WEEKLY_OFF)
      const all = [];
      this.calendarHolidays &&
        this.calendarHolidays.forEach((user) => {
          user.unavailabilities.forEach((u) => {
            if (u.status === "WEEKLY_OFF") {
              all.push({
                date: moment(u.date),
                name: u.description || "Weekly Off",
                type: "weekly_off",
                status: "WEEKLY_OFF",
              });
            }
          });
        });
      // Remove duplicates by date
      const unique = [];
      const seen = new Set();
      all.forEach((h) => {
        const key = h.date.format("YYYY-MM-DD");
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(h);
        }
      });
      return unique;
    },
    leaveEntries() {
      const all = [];
      this.calendarHolidays &&
        this.calendarHolidays.forEach((user) => {
          user.unavailabilities.forEach((u) => {
            if (u.status === "LEAVE") {
              all.push({
                id: user.userId + "_" + u.date + "_" + u.session_type,
                name: user.name,
                type: u.status,
                duration: u.session_type,
                date: moment(u.start_time),
                toDate: moment(u.end_time),
                reason: u.description || "",
                shiftType: u.shiftType || "",
              });
            }
          });
        });
      const unique = [];
      const seen = new Set();
      all.forEach((h) => {
        if (!seen.has(h.id)) {
          seen.add(h.id);
          unique.push(h);
        }
      });
      return unique;
    },
    calendarDays() {
      const startOfMonth = this.currentDate.clone().startOf("month");
      const endOfMonth = this.currentDate.clone().endOf("month");

      const startDay = startOfMonth.day() === 0 ? 6 : startOfMonth.day() - 1;
      const days = [];

      // Days from previous month
      const prevMonth = this.currentDate.clone().subtract(1, "month");
      const prevMonthEnd = prevMonth.clone().endOf("month");
      for (let i = startDay - 1; i >= 0; i--) {
        const day = prevMonthEnd.clone().subtract(i, "day");
        days.push({
          day: day.date(),
          date: day,
          isCurrentMonth: false,
          isPrevMonth: true,
          isNextMonth: false,
        });
      }

      // Days of current month
      for (let d = 1; d <= endOfMonth.date(); d++) {
        const day = this.currentDate.clone().date(d);
        days.push({
          day: d,
          date: day,
          isCurrentMonth: true,
          isPrevMonth: false,
          isNextMonth: false,
        });
      }

      // Fill next month days to complete 6 rows (6*7 = 42 days)
      const remaining = 42 - days.length;
      const nextMonth = this.currentDate
        .clone()
        .add(1, "month")
        .startOf("month");
      for (let i = 0; i < remaining; i++) {
        const day = nextMonth.clone().add(i, "days");
        days.push({
          day: day.date(),
          date: day,
          isCurrentMonth: false,
          isPrevMonth: false,
          isNextMonth: true,
        });
      }

      return days;
    },
    formattedSelectedDate() {
      return this.selectedDate.format("dddd, MMMM D, YYYY");
    },
    selectedHoliday() {
      // Prefer holiday, else show weekly off if present
      const holiday = this.holidays.find((h) =>
        h.date.isSame(this.selectedDate, "day")
      );
      if (holiday) return holiday;
      const weeklyOff = this.weeklyOffs.find((w) =>
        w.date.isSame(this.selectedDate, "day")
      );
      if (weeklyOff)
        return { ...weeklyOff, name: weeklyOff.name || "Weekly Holiday" };
      return null;
    },
    selectedLeaveEntries() {
      return this.leaveEntries.filter((entry) =>
        entry.date.isSame(this.selectedDate, "day")
      );
    },
  },
  methods: {
    generateYears() {
      const years = [];
      const startYear = moment().year() - 10;
      const endYear = moment().year() + 10;
      for (let y = startYear; y <= endYear; y++) {
        years.push(y);
      }
      return years;
    },
    updateMonthYear() {
      this.currentDate = moment()
        .year(this.selectedYear)
        .month(this.selectedMonth)
        .startOf("month");

      // Optional: reset selectedDate as well
      this.selectedDate = this.currentDate.clone();

      // Emit or log first & last day of new month
      const firstDay = this.currentDate.clone().startOf("month");
      const lastDay = this.currentDate.clone().endOf("month");
      const fromDay = firstDay.format("YYYY-MM-DD");
      const toDay = lastDay.format("YYYY-MM-DD");

      this.getCalendarHolidaysMethod(fromDay, toDay);
    },
    isSameDate(d1, d2) {
      return moment(d1).isSame(d2, "day");
    },
    isToday(date) {
      return moment().isSame(date, "day");
    },
    isHoliday(date) {
      // Check if the date is a holiday or a weekly off
      return (
        this.holidays.some((h) => h.date.isSame(date, "day")) ||
        this.weeklyOffs.some((w) => w.date.isSame(date, "day"))
      );
    },
    hasLeave(date) {
      return this.leaveEntries.some((entry) => entry.date.isSame(date, "day"));
    },
    navigateMonth(direction) {
      if (direction === "prev") {
        this.currentDate = this.currentDate.clone().subtract(1, "month");
      } else {
        this.currentDate = this.currentDate.clone().add(1, "month");
      }

      this.selectedMonth = this.currentDate.month();
      this.selectedYear = this.currentDate.year();

      const firstDay = this.currentDate.clone().startOf("month");
      const lastDay = this.currentDate.clone().endOf("month");

      const fromDay = firstDay.format("YYYY-MM-DD");
      const toDay = lastDay.format("YYYY-MM-DD");

      this.getCalendarHolidaysMethod(fromDay, toDay);
    },
    handleDateClick(dayInfo) {
      this.selectedDate = dayInfo.date.clone();

      if (!dayInfo.isCurrentMonth) {
        // Update currentDate, selectedMonth, and selectedYear to match the clicked date
        this.currentDate = dayInfo.date.clone().startOf("month");
        this.selectedMonth = this.currentDate.month();
        this.selectedYear = this.currentDate.year();
        // Also fetch new holidays/leaves for the new month
        const fromDay = this.currentDate
          .clone()
          .startOf("month")
          .format("YYYY-MM-DD");
        const toDay = this.currentDate
          .clone()
          .endOf("month")
          .format("YYYY-MM-DD");
        this.getCalendarHolidaysMethod(fromDay, toDay);
      }
    },
    getDayClasses(dayInfo) {
      const isSelected = this.isSameDate(dayInfo.date, this.selectedDate);
      const isToday = this.isToday(dayInfo.date);
      const isHoliday = this.holidays.some((h) =>
        h.date.isSame(dayInfo.date, "day")
      );
      const isWeeklyOff = this.weeklyOffs.some((w) =>
        w.date.isSame(dayInfo.date, "day")
      );
      const hasLeave = this.hasLeave(dayInfo.date);

      let classes = [];

      if (!dayInfo.isCurrentMonth) classes.push("other-month");
      if (isSelected) classes.push("selected");
      else if (isToday) classes.push("today");
      if (hasLeave && !isSelected) classes.push("leave-day");
      else if (isHoliday && !isSelected) classes.push("holiday-day");
      else if (isWeeklyOff && !isSelected) classes.push("weeklyoff-day");

      return classes.join(" ");
    },
    getBadgeClasses(type) {
      if (type === "Sick Leave") return "sick-leave";
      if (type === "Casual Leave") return "casual-leave";
      return "annual-leave";
    },
    getInitials(name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .join("");
    },
    formatDateTime(dt) {
      if (!dt) return '';
      return moment(dt).format('DD MMM YYYY, hh:mm A');
    },
    async getCalendarHolidaysMethod(fromDate, toDate) {
      const payload = {
        fromDate: fromDate,
        toDate: toDate,
      };
      const userData = sessionStorage.getItem("ngb:user");
      const userId = JSON.parse(userData).userId;
      const userRoles = JSON.parse(userData).roles;
      const hasAdminRole = userRoles.some((role) => role.id === 1);

      if (!hasAdminRole) {
        payload.userId = userId;
      }
      
      try {
        await this.$store.dispatch(
          "holidayManagement/getCalendarHolidaysAction",
          payload
        );
      } catch (error) {
        throw Error(
          error.message.errors[0].defaultUserMessage ||
            `Api failed with status code: ${error.message.httpStatusCode}`
        );
      }
    },
  },
  watch: {},
  async mounted() {
    const fromDate = this.currentDate
      .clone()
      .startOf("month")
      .format("YYYY-MM-DD");
    const toDate = this.currentDate.clone().endOf("month").format("YYYY-MM-DD");
    await this.getCalendarHolidaysMethod(fromDate, toDate);
  },
};
</script>

<style scoped>
.month-select,
.year-select {
  padding: 6px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  background-color: white;
  outline: none;
  width: 100px;
  appearance: none;
}
/* Base Styles */
.leave-calendar {
  background-color: #f8fafc;
  padding: 18px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.calendar-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;
}

@media (max-width: 1024px) {
  .calendar-grid {
    grid-template-columns: 1fr;
  }
  .day-headers,
  .calendar-days {
    grid-template-columns: repeat(7, minmax(32px, 1fr));
    gap: 2px;
  }
  .day-header,
  .day-cell {
    min-width: 32px;
    width: 100%;
    padding: 0;
  }
}

/* Card Styles */
.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.card-header {
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f1f5f9;
}

.card-content {
  padding: 24px;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px 0;
}

/* Calendar Section */
.month-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #64748b;
}

.nav-button:hover {
  background-color: #f1f5f9;
  color: #334155;
}

.icon {
  width: 16px;
  height: 16px;
}

.month-title {
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  margin: 0;
}

/* Day Headers */
.day-headers {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.day-header {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

/* Calendar Days */
.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.day-cell {
  height: 40px;
}

.day-button {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
}

.day-button:hover {
  background-color: #eff6ff;
  color: #1e40af;
}

.day-button.other-month {
  color: #9ca3af;
  font-weight: 400;
}

.day-button.other-month:hover {
  background-color: #f9fafb;
  color: #6b7280;
}

.day-button.selected {
  background-color: #3b82f6;
  color: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.day-button.selected:hover {
  background-color: #2563eb;
}

.day-button.today {
  background-color: #dbeafe;
  color: #1d4ed8;
  font-weight: 600;
}

.day-button.today.other-month {
  background-color: #f3f4f6;
  color: #6b7280;
  font-weight: 500;
}

.day-button.holiday {
  color: #dc2626;
  font-weight: 600;
}

.day-button.holiday.other-month {
  color: #f87171;
  font-weight: 500;
}

.day-button.holiday:hover {
  background-color: #fef2f2;
}

.day-button.has-leave {
  background-color: #fff7ed;
  color: #ea580c;
}

.day-button.has-leave.other-month {
  color: #fb923c;
}

.day-button.has-leave:hover {
  background-color: #fed7aa;
}

.day-button.leave-day {
  color: #dc2626;
  font-weight: 600;
}
.day-button.leave-day.other-month {
  color: #f87171;
}
.day-button.leave-day:hover {
  background-color: #fecaca;
}

.day-button.holiday-day {
  color: #ffa500;
  font-weight: 600;
}
.day-button.holiday-day.other-month {
  color: #ffa500;
}
.day-button.holiday-day:hover {
  background-color: #bfdbfe;
}

.day-button.weeklyoff-day {
  color: #16a34a;
  font-weight: 600;
}
.day-button.weeklyoff-day.other-month {
  color: #4ade80;
}
.day-button.weeklyoff-day:hover {
  background-color: #bbf7d0;
}

/* Legend */
.legend {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.holiday-dot {
  background-color: #dc2626;
}

.holiday-legend span {
  color: #dc2626;
  font-weight: 500;
}

.legend-text {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

/* Details Section */
.details-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.selected-date-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.calendar-icon {
  width: 20px;
  height: 20px;
  color: #3b82f6;
}

.selected-date-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.holiday-banner {
  margin-top: 16px;
  padding: 12px;
  background-color: #fff7ed; /* amber-50 */
  border: 1px solid #fde68a; /* amber-200 */
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.holiday-indicator {
  width: 8px;
  height: 8px;
  background-color: #ffa500; /* orange for holiday */
  border-radius: 50%;
}
.holiday-text {
  color: #b45309; /* amber-700 */
  font-weight: 600;
  font-size: 14px;
}

.weeklyoff-banner {
  margin-top: 16px;
  padding: 12px;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.weeklyoff-indicator {
  width: 8px;
  height: 8px;
  background-color: #16a34a;
  border-radius: 50%;
}
.weeklyoff-text {
  color: #166534;
  font-weight: 600;
  font-size: 14px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.users-icon {
  width: 20px;
  height: 20px;
  color: #3b82f6;
}

/* Leave Entries */
.leave-entries {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.leave-entry {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.avatar {
  width: 32px;
  height: 32px;
  background-color: #dbeafe;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.avatar-text {
  color: #1d4ed8;
  font-weight: 600;
  font-size: 14px;
}

.entry-details {
  flex: 1;
  min-width: 0;
}

.entry-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.employee-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 14px;
}

.leave-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.leave-badge.sick-leave {
  background-color: #fef2f2;
  color: #b91c1c;
}

.leave-badge.casual-leave {
  background-color: #fff7ed;
  color: #c2410c;
}

.leave-badge.annual-leave {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.duration-info {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #6b7280;
}

.clock-icon {
  width: 12px;
  height: 12px;
}

.duration-text {
  font-size: 12px;
}

.entry-reason {
  color: #6b7280;
  font-size: 14px;
  margin: 4px 0 0 0;
  line-height: 1.4;
}

/* No Activity State */
.no-activity {
  text-align: center;
  padding: 48px 24px;
}

.no-activity-icon {
  width: 48px;
  height: 48px;
  background-color: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px auto;
}

.empty-calendar-icon {
  width: 24px;
  height: 24px;
  color: #9ca3af;
}

.no-activity-text {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .leave-calendar {
    padding: 8px;
  }

  .card-header,
  .card-content {
    padding: 10px;
  }

  .calendar-section {
    min-width: 0;
  }

  .day-headers,
  .calendar-days {
    grid-template-columns: repeat(7, minmax(28px, 1fr));
    gap: 1px;
  }
  .day-header,
  .day-cell {
    min-width: 28px;
    width: 100%;
    padding: 0;
  }

  .entry-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .selected-date-title {
    font-size: 16px;
  }
}
</style>
